import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Plus, ClipboardPaste, Save, Loader2, Mic, Sparkles, Trash2 } from 'lucide-react';
import RitualDraftPanel from './RitualDraftPanel';
import RitualCardList from './RitualCardList';
import RitualBulkBar from './RitualBulkBar';

const BLANK = {
  title: '', description: '', suggested_window: 'any', duration_minutes: '',
  anchor: '', intention: '', landing: '', prompts: '', category: '', subcategory: '',
  audio_url: '', audio_filename: '',
  source_module_id: '', source_folder_id: '', order: 0, active: true,
};

const Field = ({ label, hint, children }) => (
  <label className="block space-y-1">
    <span className="text-xs text-slate-300">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
  </label>
);

const input = `w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm
               text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/70`;

/**
 * Admin → Ritual Cards. The library the Habit Builder draws from.
 * A card tagged with a section stays invisible to members until they finish that section.
 */
const CardEditor = ({ card, sections, categories = [], subcategories = [], onSaved, onCancel }) => {
  const [form, setForm] = useState(card ? {
    ...BLANK, ...card,
    duration_minutes: card.duration_minutes || '',
    prompts: (card.prompts || []).join('\n'),
    source_module_id: card.source_module_id || '',
    source_folder_id: card.source_folder_id || '',
  } : BLANK);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const folders = (sections.modules || [])
    .find((m) => m.id === form.source_module_id)?.folders || [];

  const save = async () => {
    if (!form.title.trim()) return toast.error('The card needs a title');
    setBusy(true);
    const payload = {
      ...form,
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      prompts: form.prompts.split('\n').map((p) => p.trim()).filter(Boolean),
      source_module_id: form.source_module_id || null,
      source_folder_id: form.source_folder_id || null,
      order: Number(form.order) || 0,
    };
    delete payload.id;
    delete payload.source;
    delete payload.created_at;
    delete payload.updated_at;
    try {
      if (card) await axios.patch(`${API}/admin/ritual-cards/${card.id}`, payload);
      else await axios.post(`${API}/admin/ritual-cards`, payload);
      toast.success(card ? 'Card updated' : 'Card created');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that card');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-amber-400/30 bg-slate-900/60 p-5 space-y-4"
         data-testid="ritual-card-editor">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Title">
          <input className={input} value={form.title} onChange={set('title')}
                 placeholder="Morning breathwork" data-testid="card-title-input" />
        </Field>
        <Field label="Family"
               hint="Members browse the library by family, closed until they open one">
          <input className={input} value={form.category} onChange={set('category')}
                 list="ritual-card-categories" placeholder="Nature"
                 data-testid="card-category-input" />
          <datalist id="ritual-card-categories">
            {categories.map((name) => <option key={name} value={name} />)}
          </datalist>
        </Field>
      </div>

      <Field label="Sub-category"
             hint="The group inside the family — Nature → Emotional Well-Being. Leave blank to sit directly under the family.">
        <input className={input} value={form.subcategory} onChange={set('subcategory')}
               list="ritual-card-subcategories" placeholder="Emotional Well-Being"
               data-testid="card-subcategory-input" />
        <datalist id="ritual-card-subcategories">
          {subcategories.map((name) => <option key={name} value={name} />)}
        </datalist>
      </Field>

      <Field label="Description" hint="What the member is being invited to do">
        <textarea className={input} rows={3} value={form.description}
                  onChange={set('description')} data-testid="card-description-input" />
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Suggested window" hint="A suggestion only — members may place it anywhere">
          <select className={input} value={form.suggested_window}
                  onChange={set('suggested_window')} data-testid="card-window-select">
            <option value="any">Any window</option>
            <option value="morning">Morning</option>
            <option value="midday">Midday</option>
            <option value="evening">Evening</option>
          </select>
        </Field>
        <Field label="Duration (minutes)" hint="Leave blank and the member sets their own">
          <input type="number" min="1" className={input} value={form.duration_minutes}
                 onChange={set('duration_minutes')} data-testid="card-duration-input" />
        </Field>
      </div>

      <p className="text-[11px] text-slate-400 border-t border-slate-800 pt-3">
        Anchor · Intention · Landing — fill any of these to set them for everyone. Whatever you
        leave blank is the member&rsquo;s own work to fill in.
      </p>
      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Anchor">
          <input className={input} value={form.anchor} onChange={set('anchor')}
                 data-testid="card-anchor-input" />
        </Field>
        <Field label="Intention">
          <input className={input} value={form.intention} onChange={set('intention')}
                 data-testid="card-intention-input" />
        </Field>
        <Field label="Landing">
          <input className={input} value={form.landing} onChange={set('landing')}
                 data-testid="card-landing-input" />
        </Field>
      </div>

      <Field label="Reflection prompts" hint="One per line. Completing the ritual opens these in the member's Reflection Journal.">
        <textarea className={input} rows={2} value={form.prompts} onChange={set('prompts')}
                  data-testid="card-prompts-input" />
      </Field>

      <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-4 space-y-3">
        <div>
          <h4 className="text-sm font-medium text-white">Recording</h4>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Upload a recorded guided meditation and members play it inside the ritual as they
            practise. MP3, MP4, WAV, M4A or AAC, up to 350MB. Re-uploading reaches everyone who
            already added this ritual.
          </p>
        </div>
        <input
          type="file"
          accept=".mp3,.mp4,.wav,.m4a,.aac"
          disabled={uploading}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setUploading(true);
            setProgress(0);
            try {
              const body = new FormData();
              body.append('file', file);
              const { data } = await axios.post(`${API}/upload-meditation`, body, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (event) => setProgress(
                  Math.round((event.loaded * 100) / (event.total || 1))),
              });
              setForm((f) => ({
                ...f, audio_url: data.file_path, audio_filename: data.filename }));
              toast.success('Recording uploaded');
            } catch (err) {
              toast.error(err.response?.data?.detail || 'That recording could not be uploaded');
            } finally {
              setUploading(false);
            }
          }}
          className="w-full text-sm text-slate-300 cursor-pointer"
          data-testid="card-audio-upload"
        />
        {uploading && (
          <div className="space-y-1" data-testid="card-audio-progress">
            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-amber-400 transition-all duration-200"
                   style={{ width: `${progress}%` }} />
            </div>
            <p className="text-[11px] text-slate-400">Uploading… {progress}%</p>
          </div>
        )}
        {form.audio_url && !uploading && (
          <div className="space-y-2" data-testid="card-audio-preview">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-xs text-emerald-200 min-w-0">
                <Mic className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{form.audio_filename || 'Recording attached'}</span>
              </span>
              <button onClick={() => setForm((f) => ({
                        ...f, audio_url: '', audio_filename: '' }))}
                      className="text-slate-500 hover:text-rose-300 transition-colors"
                      data-testid="card-audio-remove">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <audio controls src={form.audio_url} className="w-full h-9" />
          </div>
        )}
        <Field label="Or paste an external URL">
          <input className={input} value={form.audio_url} onChange={set('audio_url')}
                 placeholder="https://…" data-testid="card-audio-url-input" />
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-4 border-t border-slate-800 pt-4">
        <Field label="Source module">
          <select className={input} value={form.source_module_id}
                  onChange={(e) => setForm((f) => ({
                    ...f, source_module_id: e.target.value, source_folder_id: '' }))}
                  data-testid="card-module-select">
            <option value="">No module</option>
            {(sections.modules || []).map((m) => (
              <option key={m.id} value={m.id}>Module {m.order} · {m.title}</option>
            ))}
          </select>
        </Field>
        <Field label="Source section"
               hint="Members cannot see this card until they finish this section">
          <select className={input} value={form.source_folder_id}
                  onChange={set('source_folder_id')} disabled={!form.source_module_id}
                  data-testid="card-folder-select">
            <option value="">No section gate</option>
            {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="Order">
          <input type="number" className={input} value={form.order} onChange={set('order')}
                 data-testid="card-order-input" />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button onClick={save} disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2
                           text-sm font-semibold text-slate-950 hover:bg-amber-400
                           disabled:opacity-50 transition-colors"
                data-testid="card-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {card ? 'Save card' : 'Create card'}
        </button>
        <button onClick={onCancel}
                className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300
                           hover:border-slate-500 transition-colors"
                data-testid="card-cancel-btn">
          Cancel
        </button>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300 ml-auto">
          <input type="checkbox" checked={form.active} onChange={set('active')}
                 data-testid="card-active-toggle" />
          Visible to members
        </label>
      </div>
    </div>
  );
};

const ImportPanel = ({ sections, categories = [], onImported }) => {
  const [text, setText] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [folderId, setFolderId] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const folders = (sections.modules || []).find((m) => m.id === moduleId)?.folders || [];

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/ritual-cards/import`, {
        text, category, source_module_id: moduleId || null, source_folder_id: folderId || null,
      });
      toast.success(`${data.count} card${data.count === 1 ? '' : 's'} created`);
      setText('');
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nothing could be read from that');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5 space-y-3"
         data-testid="ritual-import-panel">
      <div>
        <h3 className="text-sm font-semibold text-white">Paste in from the program</h3>
        <p className="text-[11px] text-slate-400 mt-1">
          One ritual per paragraph — title on the first line, the rest becomes the description,
          and a line like <span className="text-slate-200">20m</span> is read as the duration.
          A CSV with a <span className="text-slate-200">title,description,window,duration</span>
          header works too.
        </p>
      </div>
      <textarea className={input} rows={7} value={text} onChange={(e) => setText(e.target.value)}
                placeholder={'Walking Meditation\nWalk without a destination.\n20m\n\nEnergy Hygiene\nClear the field before sleep.'}
                data-testid="ritual-import-text" />
      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Category (applies to every card)">
          <input className={input} value={category} onChange={(e) => setCategory(e.target.value)}
                 list="ritual-import-categories" placeholder="Breathwork"
                 data-testid="import-category-input" />
          <datalist id="ritual-import-categories">
            {categories.map((name) => <option key={name} value={name} />)}
          </datalist>
        </Field>
        <Field label="Source module (applies to every card)">
          <select className={input} value={moduleId}
                  onChange={(e) => { setModuleId(e.target.value); setFolderId(''); }}
                  data-testid="import-module-select">
            <option value="">No module</option>
            {(sections.modules || []).map((m) => (
              <option key={m.id} value={m.id}>Module {m.order} · {m.title}</option>
            ))}
          </select>
        </Field>
        <Field label="Source section (gates visibility)">
          <select className={input} value={folderId} onChange={(e) => setFolderId(e.target.value)}
                  disabled={!moduleId} data-testid="import-folder-select">
            <option value="">No section gate</option>
            {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>
      </div>
      <button onClick={run} disabled={busy || !text.trim()}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-700 px-4 py-2 text-sm
                         text-white hover:bg-slate-600 disabled:opacity-40 transition-colors"
              data-testid="ritual-import-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardPaste className="w-4 h-4" />}
        Create cards
      </button>
    </div>
  );
};

export const AdminRitualCardsTab = () => {
  const [cards, setCards] = useState([]);
  const [sections, setSections] = useState({ modules: [] });
  const [editing, setEditing] = useState(null);   // card object | 'new' | null
  const [ticked, setTicked] = useState([]);
  const [importing, setImporting] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/ritual-cards`).then((r) => setCards(r.data.cards)).catch(() => {});
    axios.get(`${API}/admin/ritual-cards/sections`).then((r) => setSections(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (card) => {
    if (!window.confirm(`Delete “${card.title}”? Members who already adopted it keep their own copy.`)) return;
    await axios.delete(`${API}/admin/ritual-cards/${card.id}`).catch(() => {});
    if (editing?.id === card.id) setEditing(null);
    load();
  };

  const reorder = async (ids) => {
    setCards((prev) => ids.map((id) => prev.find((c) => c.id === id)).filter(Boolean));
    await axios.patch(`${API}/admin/ritual-cards/reorder`, { ids }).catch(() => {
      toast.error('That order could not be saved');
      load();
    });
  };

  const names = (key) => [...new Set(cards.map((c) => c[key]).filter(Boolean))].sort();

  return (
    <div className="space-y-5" data-testid="admin-ritual-cards-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Ritual Cards</h2>
          <p className="text-xs text-slate-400 mt-1">
            The library behind the Sacred Habit Stack. The gate section for the whole feature is{' '}
            <span className="text-slate-200">{sections.gate?.name || 'Sacred Habits (not uploaded yet)'}</span>.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setDrafting((v) => !v); setImporting(false); }}
                  className="inline-flex items-center gap-2 rounded-lg border border-amber-500/40
                             px-4 py-2 text-sm text-amber-200 hover:border-amber-400
                             transition-colors"
                  data-testid="toggle-draft-panel">
            <Sparkles className="w-4 h-4" /> Draft with AI
          </button>
          <button onClick={() => { setImporting((v) => !v); setDrafting(false); }}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-700
                             px-4 py-2 text-sm text-slate-200 hover:border-slate-500
                             transition-colors"
                  data-testid="toggle-import-panel">
            <ClipboardPaste className="w-4 h-4" /> Paste in
          </button>
          <button onClick={() => { setEditing('new'); setTicked([]); }}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2
                             text-sm font-semibold text-slate-950 hover:bg-amber-400
                             transition-colors"
                  data-testid="new-ritual-card-btn">
            <Plus className="w-4 h-4" /> New card
          </button>
        </div>
      </div>

      {drafting && <RitualDraftPanel sections={sections}
                                     onCreated={() => { setDrafting(false); load(); }} />}

      {importing && <ImportPanel sections={sections} categories={names('category')}
                                 onImported={() => { setImporting(false); load(); }} />}

      <div className="grid lg:grid-cols-[22rem_1fr] gap-5 items-start">
        <div className="space-y-3">
          {ticked.length > 0 && (
            <RitualBulkBar ids={ticked} categories={names('category')}
                           subcategories={names('subcategory')}
                           onClear={() => setTicked([])}
                           onDone={() => { setTicked([]); load(); }} />
          )}
          <RitualCardList
            cards={cards}
            selectedId={editing && editing !== 'new' ? editing.id : null}
            ticked={ticked}
            onSelect={(card) => setEditing(card)}
            onTick={(id, on) => setTicked((prev) =>
              on ? [...prev, id] : prev.filter((x) => x !== id))}
            onDelete={remove}
            onReorder={reorder}
          />
        </div>

        <div className="lg:sticky lg:top-4">
          {editing ? (
            <CardEditor key={editing === 'new' ? 'new' : editing.id}
                        card={editing === 'new' ? null : editing} sections={sections}
                        categories={names('category')} subcategories={names('subcategory')}
                        onCancel={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }} />
          ) : (
            <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/30
                            px-6 py-16 text-center"
                 data-testid="ritual-editor-empty">
              <p className="text-sm text-slate-400">
                Pick a card on the left to edit it here, or start a new one.
              </p>
              <p className="text-[11px] text-slate-500 mt-2">
                {cards.length} cards · {names('category').length} families
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRitualCardsTab;
